import { ReactNode, useState } from "react";
import { Box, Button, CircularProgress, ClickAwayListener, TextField, Typography } from "@mui/material";
import { SuggestionsGrid, SuggestionCard, SuggestionIconBadge } from "./ChatMessage.styled";
import { CONDUCCO_PRIMARY, INSIGHTS_FONT } from "./tokens";
import {
  PendenciasIcon,
  SearchCpfIcon,
  PerformanceIcon,
  AnomaliasIcon,
  UltimaExportacaoIcon,
  ExecutivoIcon,
} from "./insightsIcons";
import {
  getAnomalias,
  getInsightsErrorMessage,
  getPendenciasDoDia,
  getPerformance,
  getResumoExecutivo,
  getUltimaExportacao,
  investigarCpf,
  IInsightsPrefabKind,
  IInsightsPrefabResponse,
} from "v2/services/insights.service";

interface PrefabButtonConfig {
  id: IInsightsPrefabKind;
  icon: ReactNode;
  label: string;
  needsCpf?: boolean;
  enabled: boolean;
}

const PREFAB_ICON_SX = { color: CONDUCCO_PRIMARY, fontSize: 18 };

// DT_MOVIMENTACOES_ESTADO tem bug de reconciliação (pendências fantasma
// inflam contagens) — Pendências/Performance/Anomalias/Resumo executivo saem
// do ar até o DAG ser corrigido. Investigar CPF e Última exportação usam
// fontes confiáveis (timeline + eventos exportados) e continuam ativas.
const PREFAB_BUTTONS: PrefabButtonConfig[] = [
  { id: "pendencias", icon: <PendenciasIcon sx={PREFAB_ICON_SX} />, label: "Pendências do dia", enabled: false },
  { id: "investigar-cpf", icon: <SearchCpfIcon sx={PREFAB_ICON_SX} />, label: "Investigar CPF", needsCpf: true, enabled: true },
  { id: "performance", icon: <PerformanceIcon sx={PREFAB_ICON_SX} />, label: "Performance", enabled: false },
  { id: "anomalias", icon: <AnomaliasIcon sx={PREFAB_ICON_SX} />, label: "Anomalias", enabled: false },
  { id: "exportacao", icon: <UltimaExportacaoIcon sx={PREFAB_ICON_SX} />, label: "Última exportação", enabled: true },
  { id: "resumo-executivo", icon: <ExecutivoIcon sx={PREFAB_ICON_SX} />, label: "Resumo executivo", enabled: false },
];

interface ChatSuggestionsProps {
  workspaceId: string;
  disabled?: boolean;
  onResult: (label: string, response: IInsightsPrefabResponse) => void;
  onError: (message: string) => void;
}

export function ChatSuggestions({
  workspaceId,
  disabled,
  onResult,
  onError,
}: ChatSuggestionsProps) {
  const [loadingId, setLoadingId] = useState<IInsightsPrefabKind | null>(null);
  const [showCpfInput, setShowCpfInput] = useState(false);
  const [cpfInput, setCpfInput] = useState("");

  const runPrefab = async (id: IInsightsPrefabKind, cpf?: string) => {
    setLoadingId(id);
    try {
      let response: IInsightsPrefabResponse;
      switch (id) {
        case "pendencias":
          response = await getPendenciasDoDia(workspaceId);
          break;
        case "investigar-cpf":
          response = await investigarCpf(workspaceId, cpf!);
          break;
        case "performance":
          response = await getPerformance(workspaceId);
          break;
        case "anomalias":
          response = await getAnomalias(workspaceId);
          break;
        case "exportacao":
          response = await getUltimaExportacao(workspaceId);
          break;
        case "resumo-executivo":
          response = await getResumoExecutivo(workspaceId);
          break;
      }
      const label = PREFAB_BUTTONS.find((button) => button.id === id)!.label;
      onResult(label, response);
      closeCpfInput();
    } catch (error: any) {
      onError(getInsightsErrorMessage(error, "Erro ao processar solicitação"));
    } finally {
      setLoadingId(null);
    }
  };

  const closeCpfInput = () => {
    setShowCpfInput(false);
    setCpfInput("");
  };

  const handleClick = (button: PrefabButtonConfig) => {
    if (disabled || loadingId) return;
    if (button.needsCpf) {
      setShowCpfInput(true);
      return;
    }
    closeCpfInput();
    runPrefab(button.id);
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <SuggestionsGrid>
        {PREFAB_BUTTONS.filter((button) => button.enabled).map((button) => (
          <SuggestionCard
            key={button.id}
            onClick={() => handleClick(button)}
            sx={{
              opacity: disabled || loadingId ? 0.5 : 1,
              pointerEvents: disabled || loadingId ? "none" : "auto",
            }}
          >
            {loadingId === button.id ? (
              <SuggestionIconBadge>
                <CircularProgress size={14} sx={{ color: CONDUCCO_PRIMARY }} />
              </SuggestionIconBadge>
            ) : (
              <SuggestionIconBadge>{button.icon}</SuggestionIconBadge>
            )}
            <Typography
              variant="body2"
              sx={{ color: "text.primary", fontFamily: INSIGHTS_FONT, fontWeight: 500 }}
            >
              {button.label}
            </Typography>
          </SuggestionCard>
        ))}
      </SuggestionsGrid>

      {showCpfInput && (
        <ClickAwayListener onClickAway={closeCpfInput}>
          <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
            <TextField
              size="small"
              placeholder="Digite o CPF"
              value={cpfInput}
              onChange={(e) => setCpfInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") closeCpfInput();
              }}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&.Mui-focused fieldset": {
                    borderColor: CONDUCCO_PRIMARY,
                    boxShadow: "0 0 0 3px rgba(48,36,225,0.15)",
                  },
                },
              }}
            />
            <Button
              variant="contained"
              disabled={!cpfInput.trim() || loadingId === "investigar-cpf"}
              onClick={() => runPrefab("investigar-cpf", cpfInput.trim())}
              sx={{
                backgroundColor: CONDUCCO_PRIMARY,
                fontFamily: INSIGHTS_FONT,
                textTransform: "none",
                "&:hover": { backgroundColor: CONDUCCO_PRIMARY, opacity: 0.9 },
              }}
            >
              Buscar
            </Button>
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
}
